// src/services/enhancedProductService.ts
//
// A Car is a single physical vehicle — one VIN, one branch, one status. There is
// no per-warehouse quantity to allocate and no "variant" array of separately
// priced/stocked SKUs. Availability is driven entirely by `status`
// (CarStatus: In Stock / Reserved / Sold / Delivered / In Transit).
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { Product, ProductFormData, ProductWithDetails, CAR_CONDITIONS } from '../../../enums/product';
import { CategoryService } from '../category/categoryService';
import { AuditService } from '../audit/auditService';
import { WarehouseService } from '../branch/branchService';

const PRE_OWNED_CONDITIONS = CAR_CONDITIONS.filter(c => c !== 'Brand New');

const PRODUCTS_COLLECTION = 'products';

export interface PaginatedProductsResult {
  products: Product[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export class ProductService {

  /* ===================== IMAGE HELPERS ===================== */

  static async uploadImages(files: File[], productId?: string): Promise<string[]> {
    return Promise.all(
      files.map(async (file) => {
        const timestamp = Date.now();
        const storageRef = ref(storage, `products/${productId || 'temp'}/${timestamp}_${file.name}`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      })
    );
  }

  static async deleteImages(imageUrls: string[]): Promise<void> {
    await Promise.all(
      imageUrls.map(async (url) => {
        try {
          await deleteObject(ref(storage, url));
        } catch (error) {
          console.warn('Error deleting image:', error);
        }
      })
    );
  }

  /* ===================== CREATE ===================== */

  static async createProduct(formData: ProductFormData, userId: string, userName: string = ''): Promise<string> {
    try {
      const category = await CategoryService.getCategory(formData.categoryId);
      if (!category) throw new Error('Selected category does not exist');

      if (formData.subcategoryId) {
        const subcategory = await CategoryService.getCategory(formData.subcategoryId);
        if (!subcategory) throw new Error('Selected subcategory does not exist');
      }

      const discount = parseFloat(formData.discount) || 0;
      if (discount < 0 || discount > 100) throw new Error('Discount must be between 0 and 100');

      if (!formData.branchId) throw new Error('A branch is required');
      const branch = await WarehouseService.getWarehouse(formData.branchId);
      if (!branch) throw new Error('Selected branch does not exist');

      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        discount,
        unitCost: parseFloat(formData.unitCost) || 0,
        categoryId: formData.categoryId,
        subcategoryId: formData.subcategoryId || null,
        brandId: formData.brandId || null,
        modelId: formData.modelId || null,
        trimId: formData.trimId || null,
        sku: formData.sku,
        images: [] as string[],
        isFeatured: false,
        isVisible: formData.isVisible !== false,
        branchId: formData.branchId,
        branchName: branch.name,
        // ── Car specification fields ─────────────────────────────
        modelYear: formData.modelYear ? parseInt(formData.modelYear) : null,
        registrationYear: formData.registrationYear ? parseInt(formData.registrationYear) : null,
        mileageKm: formData.mileageKm ? parseInt(formData.mileageKm) : null,
        fuelType: formData.fuelType || null,
        transmission: formData.transmission || null,
        engineCapacityCc: formData.engineCapacityCc ? parseInt(formData.engineCapacityCc) : null,
        driveType: formData.driveType || null,
        seatingCapacity: formData.seatingCapacity ? parseInt(formData.seatingCapacity) : null,
        exteriorColor: formData.exteriorColor || null,
        interiorColor: formData.interiorColor || null,
        condition: formData.condition || 'Used - Good',
        status: formData.status || 'In Stock',
        registrationNumber: formData.registrationNumber || null,
        ownersCount: formData.ownersCount ? parseInt(formData.ownersCount) : null,
        isImported: formData.isImported || false,
        importOrderId: formData.importOrderId || null,
        countryOfOrigin: formData.countryOfOrigin || null,
        features: formData.features || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
      };

      const productRef = await addDoc(collection(db, PRODUCTS_COLLECTION), productData);
      const productId = productRef.id;

      if (formData.images.length > 0) {
        const imageUrls = await this.uploadImages(formData.images, productId);
        await updateDoc(productRef, { images: imageUrls });
      }

      await AuditService.log({
        action: 'create',
        module: 'Car Inventory',
        entityId: productId,
        entityLabel: formData.name,
        description: `Added new car "${formData.name}" (VIN: ${formData.sku || 'N/A'}) to inventory`,
        userId,
        userName: userName || 'Admin',
      });

      return productId;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  /* ===================== IMPORT STATUS ===================== */

  // Flips a Car from 'In Transit (Import)' to 'In Stock' once it has
  // physically arrived — used when an Import Order used/individual line is
  // received. A lightweight status-only update (unlike updateProduct, which
  // requires a full form payload) since nothing else about the car changed.
  static async markAsInStock(productId: string): Promise<void> {
    try {
      await updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
        status: 'In Stock', updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error marking car as in stock:', error);
      throw error;
    }
  }

  /* ===================== BRANCH ASSIGNMENT ===================== */

  // Moves a single car to a different branch/show room. A car has exactly
  // one branch, so "moving" it is just reassigning `branchId` — there is no
  // quantity to split or transfer, unlike the legacy multi-unit warehouse model.
  static async updateProductBranch(
    productId: string,
    productName: string,
    toBranchId: string,
    toBranchName: string,
    userId: string = '',
    userName: string = ''
  ): Promise<void> {
    try {
      await updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
        branchId: toBranchId,
        branchName: toBranchName,
        updatedAt: serverTimestamp(),
      });
      await AuditService.log({
        action: 'update',
        module: 'Car Inventory',
        entityId: productId,
        entityLabel: productName,
        description: `Moved car '${productName}' to branch '${toBranchName}'`,
        userId,
        userName: userName || 'Admin',
      });
    } catch (error) {
      console.error('Error moving car to branch:', error);
      throw error;
    }
  }

  /* ===================== FEATURED ===================== */

  static async toggleFeaturedProduct(productId: string, isFeatured: boolean): Promise<void> {
    try {
      await updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
        isFeatured, updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error toggling featured status:', error);
      throw error;
    }
  }

  static async toggleVisibility(productId: string, isVisible: boolean): Promise<void> {
    try {
      await updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
        isVisible, updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  static async getFeaturedProducts(): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION),
        where('isFeatured', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.mapDocToProduct(d));
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  }

  static async getFeaturedProductsWithDetails(): Promise<ProductWithDetails[]> {
    try {
      const products = await this.getFeaturedProducts();
      return Promise.all(
        products.map(async product => {
          const category = product.categoryId ? await CategoryService.getCategory(product.categoryId) : null;
          const subcategory = product.subcategoryId ? await CategoryService.getCategory(product.subcategoryId) : null;
          return { ...product, category: category || undefined, subcategory: subcategory || undefined };
        })
      );
    } catch (error) {
      console.error('Error fetching featured products with details:', error);
      throw error;
    }
  }

  /* ===================== UPDATE ===================== */

  static async updateProduct(
    productId: string, formData: ProductFormData, existingImages: string[] = [], editedByUserId: string = '',
    editedByUserName: string = ''
  ): Promise<void> {
    try {
      const currentProduct = await this.getProduct(productId);
      if (!currentProduct) throw new Error('Product not found');

      const category = await CategoryService.getCategory(formData.categoryId);
      if (!category) throw new Error('Selected category does not exist');

      if (formData.subcategoryId) {
        const subcategory = await CategoryService.getCategory(formData.subcategoryId);
        if (!subcategory) throw new Error('Selected subcategory does not exist');
      }

      const discount = parseFloat(formData.discount) || 0;
      if (discount < 0 || discount > 100) throw new Error('Discount must be between 0 and 100');

      if (!formData.price || parseFloat(formData.price) <= 0) {
        throw new Error('Valid price is required');
      }
      if (!formData.sku.trim()) throw new Error('VIN / Chassis No. is required');
      if (!formData.branchId) throw new Error('A branch is required');
      const branch = await WarehouseService.getWarehouse(formData.branchId);
      if (!branch) throw new Error('Selected branch does not exist');

      const productRef = doc(db, PRODUCTS_COLLECTION, productId);

      let newImageUrls: string[] = [];
      if (formData.images.length > 0) {
        newImageUrls = await this.uploadImages(formData.images, productId);
      }
      const allImages = [...existingImages, ...newImageUrls].slice(0, 5);

      await updateDoc(productRef, {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        discount,
        unitCost: parseFloat(formData.unitCost) || 0,
        categoryId: formData.categoryId,
        subcategoryId: formData.subcategoryId || null,
        brandId: formData.brandId || null,
        modelId: formData.modelId || null,
        trimId: formData.trimId || null,
        sku: formData.sku,
        images: allImages,
        branchId: formData.branchId,
        branchName: branch.name,
        modelYear: formData.modelYear ? parseInt(formData.modelYear) : null,
        registrationYear: formData.registrationYear ? parseInt(formData.registrationYear) : null,
        mileageKm: formData.mileageKm ? parseInt(formData.mileageKm) : null,
        fuelType: formData.fuelType || null,
        transmission: formData.transmission || null,
        engineCapacityCc: formData.engineCapacityCc ? parseInt(formData.engineCapacityCc) : null,
        driveType: formData.driveType || null,
        seatingCapacity: formData.seatingCapacity ? parseInt(formData.seatingCapacity) : null,
        exteriorColor: formData.exteriorColor || null,
        interiorColor: formData.interiorColor || null,
        condition: formData.condition || 'Used - Good',
        status: formData.status || 'In Stock',
        registrationNumber: formData.registrationNumber || null,
        ownersCount: formData.ownersCount ? parseInt(formData.ownersCount) : null,
        isImported: formData.isImported || false,
        importOrderId: formData.importOrderId || null,
        countryOfOrigin: formData.countryOfOrigin || null,
        features: formData.features || [],
        isVisible: formData.isVisible !== false,
        updatedAt: serverTimestamp(),
      });

      await AuditService.log({
        action: 'update',
        module: 'Car Inventory',
        entityId: productId,
        entityLabel: formData.name,
        description: `Updated car listing "${formData.name}" (VIN: ${formData.sku || 'N/A'})`,
        userId: editedByUserId,
        userName: editedByUserName || 'Admin',
      });
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  /* ===================== DELETE ===================== */

  static async deleteProduct(productId: string, deletedByUserId: string = '', deletedByUserName: string = ''): Promise<void> {
    try {
      const product = await this.getProduct(productId);
      if (!product) throw new Error('Product not found');

      if (product.images.length) await this.deleteImages(product.images);
      await deleteDoc(doc(db, PRODUCTS_COLLECTION, productId));

      await AuditService.log({
        action: 'delete',
        module: 'Car Inventory',
        entityId: productId,
        entityLabel: product.name,
        description: `Removed car "${product.name}" (VIN: ${product.sku || 'N/A'}) from inventory`,
        userId: deletedByUserId,
        userName: deletedByUserName || 'Admin',
      });
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  /* ===================== READ — SINGLE ===================== */

  static async getProduct(productId: string): Promise<Product | null> {
    try {
      const productSnap = await getDoc(doc(db, PRODUCTS_COLLECTION, productId));
      if (!productSnap.exists()) return null;
      return this.mapDocToProduct(productSnap);
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }

  static async getProductWithDetails(productId: string): Promise<ProductWithDetails | null> {
    try {
      const product = await this.getProduct(productId);
      if (!product) return null;
      const category = product.categoryId ? await CategoryService.getCategory(product.categoryId) : null;
      const subcategory = product.subcategoryId ? await CategoryService.getCategory(product.subcategoryId) : null;
      return { ...product, category: category || undefined, subcategory: subcategory || undefined };
    } catch (error) {
      console.error('Error fetching product with details:', error);
      throw error;
    }
  }

  /* ===================== READ — COLLECTIONS ===================== */

  /* ===================== GET PRODUCTS PAGINATED =====================
   * Seek-based pagination — any page can be fetched in any order (page 1 →
   * page 5 → page 2 all work correctly), same approach as elsewhere in the
   * admin app: fetch a lightweight cursor-seek page, then the real page.
   * ========================================================= */
  static async getProductsPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    categoryFilter?: string,
    statusFilter?: string,
    branchFilter?: string,
    featuredFilter?: string,
    brandFilter?: string,
    conditionFilter?: string
  ): Promise<PaginatedProductsResult> {
    try {
      const collectionRef = collection(db, PRODUCTS_COLLECTION);

      const filterConstraints: any[] = [];

      if (categoryFilter && categoryFilter !== 'all') {
        filterConstraints.push(where('categoryId', '==', categoryFilter));
      }
      if (statusFilter && statusFilter !== 'all') {
        filterConstraints.push(where('status', '==', statusFilter));
      }
      if (branchFilter && branchFilter !== 'all') {
        filterConstraints.push(where('branchId', '==', branchFilter));
      }
      if (featuredFilter === 'featured') {
        filterConstraints.push(where('isFeatured', '==', true));
      } else if (featuredFilter === 'nonFeatured') {
        filterConstraints.push(where('isFeatured', '==', false));
      }
      if (brandFilter && brandFilter !== 'all') {
        filterConstraints.push(where('brandId', '==', brandFilter));
      }
      if (conditionFilter === 'brand-new') {
        filterConstraints.push(where('condition', '==', 'Brand New'));
      } else if (conditionFilter === 'pre-owned') {
        // 'in' (not a range operator) can combine with orderBy('createdAt')
        // without requiring the inequality-orderBy field match that `!=` would.
        filterConstraints.push(where('condition', 'in', PRE_OWNED_CONDITIONS));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      const products: Product[] = [];
      for (const docSnap of snapshot.docs) {
        const product = this.mapDocToProduct(docSnap);

        if (searchQuery && searchQuery.trim() !== '') {
          const lower = searchQuery.toLowerCase();
          const matches =
            product.name.toLowerCase().includes(lower) ||
            product.description.toLowerCase().includes(lower) ||
            product.sku.toLowerCase().includes(lower);
          if (!matches) continue;
        }

        products.push(product);
      }

      return {
        products,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated products:', error);
      throw error;
    }
  }

  /** @deprecated Use getProductsPaginated instead for better performance */
  static async getProducts(): Promise<Product[]> {
    try {
      const q = query(collection(db, PRODUCTS_COLLECTION), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.mapDocToProduct(d));
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  static async getProductsWithDetails(): Promise<ProductWithDetails[]> {
    try {
      const products = await this.getProducts();
      return Promise.all(
        products.map(async product => {
          const category = product.categoryId ? await CategoryService.getCategory(product.categoryId) : null;
          const subcategory = product.subcategoryId ? await CategoryService.getCategory(product.subcategoryId) : null;
          return { ...product, category: category || undefined, subcategory: subcategory || undefined };
        })
      );
    } catch (error) {
      console.error('Error fetching products with details:', error);
      throw error;
    }
  }

  static async getProductsByCategory(categoryId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION), where('categoryId', '==', categoryId), orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.mapDocToProduct(d));
    } catch (error) {
      console.error('Error fetching products by category:', error);
      throw error;
    }
  }

  static async getProductsBySubcategory(subcategoryId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION), where('subcategoryId', '==', subcategoryId), orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.mapDocToProduct(d));
    } catch (error) {
      console.error('Error fetching products by subcategory:', error);
      throw error;
    }
  }

  static async getProductsByBrand(brandId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION), where('brandId', '==', brandId), orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.mapDocToProduct(d));
    } catch (error) {
      console.error('Error fetching products by brand:', error);
      throw error;
    }
  }

  /** In-stock units for a given brand+model — used to let staff pick a specific
   *  vehicle when logging a test drive request (mirrors the client site's
   *  brand/model/vehicle picker in ReserveCarModal.tsx). */
  static async getInStockByBrandModel(brandId: string, modelId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION),
        where('brandId', '==', brandId),
        where('modelId', '==', modelId),
        where('status', '==', 'In Stock'),
        limit(25)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.mapDocToProduct(d));
    } catch (error) {
      console.error('Error fetching in-stock products by brand/model:', error);
      throw error;
    }
  }

  static async getProductsByBranch(branchId: string): Promise<Product[]> {
    try {
      const q = query(
        collection(db, PRODUCTS_COLLECTION), where('branchId', '==', branchId), orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.mapDocToProduct(d));
    } catch (error) {
      console.error('Error fetching products by branch:', error);
      throw error;
    }
  }

  static async getProductsForDropdown(): Promise<Product[]> {
    try {
      const q = query(collection(db, PRODUCTS_COLLECTION), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.mapDocToProduct(d));
    } catch (error) {
      console.error('Error fetching products for dropdown:', error);
      throw error;
    }
  }

  /* ===================== SEARCH ===================== */

  static async searchProducts(searchTerm: string): Promise<Product[]> {
    try {
      const products = await this.getProducts();
      const lower = searchTerm.toLowerCase();
      return products.filter(p =>
        p.name.toLowerCase().includes(lower) ||
        p.description.toLowerCase().includes(lower) ||
        p.sku.toLowerCase().includes(lower)
      );
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  /* ===================== STATS =====================
   * Uses getCountFromServer() for count-based stats (zero doc reads) where
   * possible. Status breakdown mirrors CarStatus, the single source of truth
   * for a vehicle's availability.
   * ========================================================= */
  static async getProductStats() {
    try {
      const col = collection(db, PRODUCTS_COLLECTION);

      const [totalSnap, featuredSnap, nonFeaturedSnap, inStockSnap, reservedSnap, soldSnap, allProducts] =
        await Promise.all([
          getCountFromServer(query(col)),
          getCountFromServer(query(col, where('isFeatured', '==', true))),
          getCountFromServer(query(col, where('isFeatured', '==', false))),
          getCountFromServer(query(col, where('status', '==', 'In Stock'))),
          getCountFromServer(query(col, where('status', '==', 'Reserved'))),
          getCountFromServer(query(col, where('status', '==', 'Sold'))),
          this.getProducts(),
        ]);

      return {
        total:        totalSnap.data().count,
        featured:     featuredSnap.data().count,
        nonFeatured:  nonFeaturedSnap.data().count,
        inStock:      inStockSnap.data().count,
        reserved:     reservedSnap.data().count,
        sold:         soldSnap.data().count,
        categories:   new Set(allProducts.map(p => p.categoryId)).size,
        brands:       new Set(allProducts.filter(p => p.brandId).map(p => p.brandId)).size,
        averagePrice: allProducts.length > 0
          ? allProducts.reduce((sum, p) => sum + p.price, 0) / allProducts.length
          : 0,
      };
    } catch (error) {
      console.error('Error getting product stats:', error);
      throw error;
    }
  }

  /* ===================== IMAGE REMOVAL ===================== */

  static async removeProductImage(productId: string, imageUrl: string): Promise<void> {
    try {
      const product = await this.getProduct(productId);
      if (!product) throw new Error('Product not found');
      await this.deleteImages([imageUrl]);
      const updatedImages = product.images.filter(img => img !== imageUrl);
      await updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
        images: updatedImages, updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error removing product image:', error);
      throw error;
    }
  }

  /* ===================== PRIVATE MAP HELPER ===================== */

  private static mapDocToProduct(docSnap: QueryDocumentSnapshot<DocumentData>): Product {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name || '',
      description: data.description || '',
      price: data.price || 0,
      discount: data.discount || 0,
      unitCost: data.unitCost || 0,
      categoryId: data.categoryId || '',
      subcategoryId: data.subcategoryId || null,
      brandId: data.brandId || null,
      modelId: data.modelId || null,
      trimId: data.trimId || null,
      trimName: data.trimName || undefined,
      sku: data.sku || '',
      images: data.images || [],
      isFeatured: data.isFeatured || false,
      isVisible: data.isVisible !== false,
      branchId: data.branchId || undefined,
      branchName: data.branchName || undefined,
      modelYear: data.modelYear ?? undefined,
      registrationYear: data.registrationYear ?? undefined,
      mileageKm: data.mileageKm ?? undefined,
      fuelType: data.fuelType ?? undefined,
      transmission: data.transmission ?? undefined,
      engineCapacityCc: data.engineCapacityCc ?? undefined,
      driveType: data.driveType ?? undefined,
      seatingCapacity: data.seatingCapacity ?? undefined,
      exteriorColor: data.exteriorColor ?? undefined,
      interiorColor: data.interiorColor ?? undefined,
      condition: data.condition ?? undefined,
      status: data.status ?? 'In Stock',
      registrationNumber: data.registrationNumber ?? undefined,
      ownersCount: data.ownersCount ?? undefined,
      isImported: data.isImported ?? false,
      importOrderId: data.importOrderId ?? undefined,
      countryOfOrigin: data.countryOfOrigin ?? undefined,
      features: data.features ?? [],
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      createdBy: data.createdBy,
      // ── Sale details — written by SoldVehicleService.recordSale() but
      // previously never read back here, so a recorded sale's buyer/payment
      // info always came back blank on the next fetch even though the
      // Firestore write itself succeeded.
      buyerName: data.buyerName ?? undefined,
      buyerPhone: data.buyerPhone ?? undefined,
      buyerEmail: data.buyerEmail ?? undefined,
      buyerIdNumber: data.buyerIdNumber ?? undefined,
      buyerAddress: data.buyerAddress ?? undefined,
      finalSalePrice: data.finalSalePrice ?? undefined,
      depositAmount: data.depositAmount ?? undefined,
      paymentMethod: data.paymentMethod ?? undefined,
      deliveryMethod: data.deliveryMethod ?? undefined,
      deliveryDate: data.deliveryDate ?? undefined,
      soldByUserId: data.soldByUserId ?? undefined,
      soldByName: data.soldByName ?? undefined,
      soldAt: data.soldAt?.toDate ? data.soldAt.toDate() : undefined,
      reservationId: data.reservationId ?? undefined,
      saleNotes: data.saleNotes ?? undefined,
      salespersonId: data.salespersonId ?? undefined,
      salespersonName: data.salespersonName ?? undefined,
      tradeInId: data.tradeInId ?? undefined,
      tradeInValue: data.tradeInValue ?? undefined,
      financingProvider: data.financingProvider ?? undefined,
      financingTermMonths: data.financingTermMonths ?? undefined,
      monthlyInstallment: data.monthlyInstallment ?? undefined,
      registrationTransferStatus: data.registrationTransferStatus ?? undefined,
      warrantyMonths: data.warrantyMonths ?? undefined,
      warrantyNotes: data.warrantyNotes ?? undefined,
    };
  }
}
